/**
 * EVENTSFLO-GOOGLE-ROUTES-ADMIN-STATUS-V1
 *
 * SuperAdmin-only visibility + safe test-lookup widget for the
 * opt-in Google Routes integration. Disabled by default.
 *
 * Safety:
 *   - Hidden for non-SuperAdmin (returns `null`).
 *   - Never displays the API key (backend never returns it).
 *   - No background polling; test lookup runs only on click.
 *   - Clear text that this does not recalculate existing
 *     projects / quotes / invoices.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCopy,
  Info,
  Map,
  Play,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const ENDPOINT = "/api/admin/google-routes";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg = typeof detail === "string" ? detail : (detail.message || detail.error || `HTTP ${r.status}`);
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

function EnabledBadge({ enabled }) {
  return (
    <Badge
      className={
        enabled
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          : "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
      }
      data-testid="google-routes-enabled-badge"
    >
      {enabled ? "Enabled" : "Disabled"}
    </Badge>
  );
}

function KeyBadge({ configured }) {
  return (
    <Badge
      className={
        configured
          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          : "bg-amber-500/15 text-amber-300 border border-amber-500/30"
      }
      data-testid="google-routes-key-badge"
    >
      {configured ? "Configured" : "Missing"}
    </Badge>
  );
}

export default function GoogleRoutesStatusCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  // EVENTSFLO-GOOGLE-ROUTES-ENABLE-FLOW-V1 — enable guidance + post-enable health check state.
  const [enablePreflight, setEnablePreflight] = useState(null);
  const [enableLoading, setEnableLoading] = useState(false);
  const [enableError, setEnableError] = useState(null);
  const [enableConfirmInput, setEnableConfirmInput] = useState("");
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [healthCheckResult, setHealthCheckResult] = useState(null);
  const [healthCheckError, setHealthCheckError] = useState(null);
  const ENABLE_CONFIRM_PHRASE = "ENABLE GOOGLE ROUTES AFTER RESTART";

  // EVENTSFLO-GOOGLE-ROUTES-CACHE-INSIGHTS-V1 — read-only mini-panel state.
  const [cacheInsights, setCacheInsights] = useState(null);
  const [cacheInsightsLoading, setCacheInsightsLoading] = useState(false);
  const [cacheInsightsError, setCacheInsightsError] = useState(null);

  // EVENTSFLO-GOOGLE-ROUTES-FINGERPRINT-HISTORY-V1 — read-only validation history
  const [fpHistory, setFpHistory] = useState(null);
  const [fpHistoryLoading, setFpHistoryLoading] = useState(false);
  const [fpHistoryError, setFpHistoryError] = useState(null);
  const fpChangedToastShownRef = useRef(false);

  const loadStatus = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setStatusLoading(true);
    setStatusError(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/status`, token);
      setStatus(body);
    } catch (e) {
      setStatusError(e.message || "Failed to load status");
    } finally {
      setStatusLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const runLookup = async () => {
    if (!origin.trim() || !destination.trim()) return;
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/test-lookup`, token, {
        method: "POST",
        body: JSON.stringify({
          origin: origin.trim(),
          destination: destination.trim(),
        }),
      });
      setLookupResult(body);
      if (body.called_provider === false) {
        toast.warning(
          body.reason === "feature_flag_disabled"
            ? "Google Routes is disabled — no provider call was made."
            : "Provider not called: " + (body.reason || "unknown"),
        );
      } else if (body.ok) {
        toast.success(
          body.cached ? "Cache hit." : "Test route lookup succeeded.",
        );
      } else {
        toast.error("Provider call failed.");
      }
    } catch (e) {
      setLookupError(e.message || "Test lookup failed");
    } finally {
      setLookupLoading(false);
    }
  };

  const loadEnablePreflight = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setEnableLoading(true);
    setEnableError(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/enable-preflight`, token, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setEnablePreflight(body);
    } catch (e) {
      setEnableError(e.message || "Enable preflight failed");
    } finally {
      setEnableLoading(false);
    }
  }, [isSuperAdmin, token]);

  const runHealthCheck = async () => {
    if (!origin.trim() || !destination.trim()) {
      toast.error("Origin and destination required for health check");
      return;
    }
    setHealthCheckLoading(true);
    setHealthCheckError(null);
    setHealthCheckResult(null);
    try {
      const body = await authedFetch(
        `${ENDPOINT}/post-enable-health-check`,
        token,
        {
          method: "POST",
          body: JSON.stringify({
            origin: origin.trim(),
            destination: destination.trim(),
          }),
        },
      );
      setHealthCheckResult(body);
      if (body.ok) {
        toast.success("Health check OK — provider returned a route");
        // Refresh status so the fingerprint badge updates.
        loadStatus();
      } else {
        toast.warning(
          `Health check: ${body.reason || "not ok"}`,
        );
      }
    } catch (e) {
      setHealthCheckError(e.message || "Health check failed");
    } finally {
      setHealthCheckLoading(false);
    }
  };

  const copyEnableRunbook = () => {
    if (!enablePreflight) return;
    if (enableConfirmInput !== ENABLE_CONFIRM_PHRASE) return;
    const lines = [
      "EventsFlo Google Routes Enable Runbook",
      `current_status: ${enablePreflight.status}`,
      `enabled_currently: ${enablePreflight.enabled_currently}`,
      `api_key_configured: ${enablePreflight.api_key_configured}`,
      `api_key_source: ${enablePreflight.api_key_source}`,
      `restart_required: ${enablePreflight.restart_required}`,
      "",
      "Required env changes (set on backend host only — never in browser):",
      ...(enablePreflight.required_env_changes || []).map(
        (c) => `  - ${c.name} → ${c.target_value}${c.secret ? " (SECRET)" : ""}`,
      ),
      "",
      "Recommended steps:",
      ...(enablePreflight.recommended_steps || []),
      "",
      "Post-enable health check:",
      ...(enablePreflight.post_enable_health_check_steps || []),
    ];
    const text = lines.join("\n");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => toast.success("Enable runbook copied"),
        () => toast.error("Clipboard unavailable"),
      );
    } else {
      window.prompt("Copy the runbook text:", text);
    }
  };

  useEffect(() => {
    loadEnablePreflight();
  }, [loadEnablePreflight]);

  const loadCacheInsights = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setCacheInsightsLoading(true);
    setCacheInsightsError(null);
    try {
      const body = await authedFetch(
        `${ENDPOINT}/cache-insights?limit=5&include_expired=true`,
        token,
      );
      setCacheInsights(body);
    } catch (e) {
      setCacheInsightsError(e.message || "Cache insights failed");
    } finally {
      setCacheInsightsLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => {
    loadCacheInsights();
  }, [loadCacheInsights]);

  const loadFpHistory = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setFpHistoryLoading(true);
    setFpHistoryError(null);
    try {
      const body = await authedFetch(
        `${ENDPOINT}/fingerprint-history?limit=10`,
        token,
      );
      setFpHistory(body);
    } catch (e) {
      setFpHistoryError(e.message || "Fingerprint history failed");
    } finally {
      setFpHistoryLoading(false);
    }
  }, [isSuperAdmin, token]);

  useEffect(() => {
    loadFpHistory();
  }, [loadFpHistory]);

  // One-time passive alert when current configured key differs
  // from the most recent successful validation. Uses a ref guard
  // so it never fires twice during the same mount/session.
  useEffect(() => {
    if (!status) return;
    if (fpChangedToastShownRef.current) return;
    if (status.key_fingerprint_status === "changed_since_last_validation") {
      fpChangedToastShownRef.current = true;
      toast.warning(
        "Google Routes API key has changed since the last successful validation. Re-run the post-enable health check to confirm.",
      );
    }
  }, [status]);

  if (!isSuperAdmin) return null;

  const lookupDisabled =
    !origin.trim() || !destination.trim() || lookupLoading;

  return (
    <Card data-testid="google-routes-status-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Map className="w-4 h-4" /> Google Routes status
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          Disabled by default. Existing travel behaviour is
          unchanged unless explicitly enabled. This tool does not
          recalculate existing projects, quotes or invoices.
        </p>
      </CardHeader>
      <CardContent>
        {statusError ? (
          <div
            className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-start gap-2"
            data-testid="google-routes-status-error"
          >
            <XCircle className="w-4 h-4 mt-0.5" />
            <span>Could not load status: {statusError}</span>
          </div>
        ) : statusLoading || !status ? (
          <div className="text-sm text-zinc-400" data-testid="google-routes-status-loading">
            Loading Google Routes status…
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm"
              data-testid="google-routes-status-grid"
            >
              <div>
                <div className="text-zinc-400 text-xs">Status</div>
                <div className="mt-0.5">
                  <EnabledBadge enabled={status.enabled} />
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">API key</div>
                <div className="mt-0.5">
                  <KeyBadge configured={status.api_key_configured} />
                </div>
                <div
                  className="text-xs text-zinc-500 mt-0.5"
                  data-testid="google-routes-key-source"
                >
                  source: {status.api_key_source}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Mode / Units</div>
                <div className="text-zinc-300 mt-0.5">
                  {status.mode} · {status.units}
                  {status.region_code ? ` · ${status.region_code}` : ""}
                </div>
              </div>
              <div>
                <div className="text-zinc-400 text-xs">Cache TTL</div>
                <div className="text-zinc-300 mt-0.5">
                  {status.cache_ttl_hours}h
                </div>
              </div>
            </div>

            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-3"
              data-testid="google-routes-cache-stats"
            >
              <div>
                <div className="text-zinc-400">cache total</div>
                <div className="text-zinc-300" data-testid="google-routes-cache-total">
                  {status.cache_total_count}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">cache valid</div>
                <div className="text-zinc-300" data-testid="google-routes-cache-valid">
                  {status.cache_valid_count}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">cache expired</div>
                <div className="text-zinc-300" data-testid="google-routes-cache-expired">
                  {status.cache_expired_count}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">last cache write</div>
                <div className="text-zinc-300">
                  {fmt(status.last_cache_write_at)}
                </div>
              </div>
            </div>

            <div
              className="mt-3 text-xs text-zinc-500 flex items-start gap-2"
              data-testid="google-routes-note"
            >
              <Info className="w-3.5 h-3.5 mt-0.5" />
              <span>{status.note}</span>
            </div>

            {/* EVENTSFLO-GOOGLE-ROUTES-KEY-FINGERPRINT-V1 — tamper-evident badge */}
            <div
              className="mt-3 rounded-md border border-zinc-700/40 p-2"
              data-testid="google-routes-key-fingerprint-section"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-zinc-400">Key fingerprint:</span>
                {(() => {
                  const fp = status.key_fingerprint_status;
                  const map = {
                    no_key_configured: {
                      cls: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30",
                      label: "No key configured",
                    },
                    never_validated: {
                      cls: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
                      label: "Never validated",
                    },
                    same_as_last_validation: {
                      cls: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
                      label: "Same key as last validation",
                    },
                    changed_since_last_validation: {
                      cls: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
                      label: "Key changed since last validation",
                    },
                  };
                  const m = map[fp] || {
                    cls: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30",
                    label: fp || "unknown",
                  };
                  return (
                    <Badge
                      className={m.cls}
                      data-testid="google-routes-key-fingerprint-badge"
                    >
                      {m.label}
                    </Badge>
                  );
                })()}
                {status.current_key_fingerprint_prefix ? (
                  <span
                    className="font-mono text-zinc-400"
                    data-testid="google-routes-current-fp-prefix"
                  >
                    current: {status.current_key_fingerprint_prefix}…
                  </span>
                ) : null}
                {status.last_validated_key_fingerprint_prefix ? (
                  <span
                    className="font-mono text-zinc-400"
                    data-testid="google-routes-last-fp-prefix"
                  >
                    last: {status.last_validated_key_fingerprint_prefix}…
                  </span>
                ) : null}
                {status.last_validated_at ? (
                  <span className="text-zinc-500">
                    validated {fmt(status.last_validated_at)}
                    {status.last_validated_source
                      ? ` · source: ${status.last_validated_source}`
                      : ""}
                  </span>
                ) : null}
              </div>
              <div className="mt-1 text-xs text-zinc-500">
                {status.key_fingerprint_note}
              </div>
              <div className="mt-1 text-xs text-zinc-500 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5" />
                <span>
                  Fingerprint is a SHA-256 hash prefix of the
                  configured key. The key itself is never stored
                  or displayed.
                </span>
              </div>
            </div>
          </>
        )}

        {/* Test lookup */}
        <div className="mt-4 rounded-md border border-zinc-700/40 p-3" data-testid="google-routes-test-lookup-section">
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Map className="w-3.5 h-3.5" /> Test route lookup
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-1">
              <Label htmlFor="grs-origin" className="text-xs text-zinc-400">
                origin
              </Label>
              <Input
                id="grs-origin"
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                placeholder="e.g. Monaco Ville, Monaco"
                data-testid="google-routes-origin-input"
              />
            </div>
            <div className="md:col-span-1">
              <Label htmlFor="grs-destination" className="text-xs text-zinc-400">
                destination
              </Label>
              <Input
                id="grs-destination"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="e.g. Nice, France"
                data-testid="google-routes-destination-input"
              />
            </div>
            <Button
              onClick={runLookup}
              disabled={lookupDisabled}
              data-testid="google-routes-test-lookup-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" />
              {lookupLoading ? "Looking up…" : "Test route lookup"}
            </Button>
          </div>

          {lookupError ? (
            <div
              className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-start gap-2"
              data-testid="google-routes-lookup-error"
            >
              <XCircle className="w-4 h-4 mt-0.5" />
              <span>Lookup error: {lookupError}</span>
            </div>
          ) : null}

          {lookupResult ? (
            lookupResult.called_provider === false ? (
              <div
                className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200 flex items-start gap-2"
                data-testid="google-routes-lookup-disabled"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5" />
                <span>
                  No provider call was made. Reason:{" "}
                  <code>{lookupResult.reason}</code>. {lookupResult.note}
                </span>
              </div>
            ) : lookupResult.ok ? (
              <div
                className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs"
                data-testid="google-routes-lookup-result"
              >
                <div>
                  <div className="text-zinc-400">distance</div>
                  <div className="text-zinc-300" data-testid="google-routes-result-distance-km">
                    {lookupResult.distance_km} km
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">duration</div>
                  <div className="text-zinc-300" data-testid="google-routes-result-duration-min">
                    {lookupResult.duration_minutes} min
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">cached</div>
                  <div className="text-zinc-300" data-testid="google-routes-result-cached">
                    {lookupResult.cached ? (
                      <span className="text-emerald-300 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> yes
                      </span>
                    ) : (
                      "no"
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">provider</div>
                  <div className="text-zinc-300" data-testid="google-routes-result-provider">
                    {lookupResult.provider}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-start gap-2"
                data-testid="google-routes-lookup-failure"
              >
                <XCircle className="w-4 h-4 mt-0.5" />
                <span>
                  Provider call failed. Reason: <code>{lookupResult.reason}</code>.
                </span>
              </div>
            )
          ) : null}
        </div>

        <div className="mt-3 flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={loadStatus}
            disabled={statusLoading}
            data-testid="google-routes-refresh-status-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Refresh status
          </Button>
        </div>

        {/* EVENTSFLO-GOOGLE-ROUTES-ENABLE-FLOW-V1 — Enable guidance + health check */}
        <div
          className="mt-5 rounded-md border border-zinc-700/40 p-3"
          data-testid="google-routes-enable-guidance"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Enable guidance
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadEnablePreflight}
              disabled={enableLoading}
              data-testid="google-routes-refresh-enable-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            This page does not enable Google Routes automatically.
            It provides the runbook to enable Google Routes safely
            and validates the integration after restart. No API key
            is ever displayed.
          </p>

          {enableError ? (
            <div
              className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300"
              data-testid="google-routes-enable-error"
            >
              {enableError}
            </div>
          ) : !enablePreflight ? (
            <div className="text-xs text-zinc-500">Loading enable guidance…</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge
                  className={
                    enablePreflight.status === "already_enabled"
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : enablePreflight.status === "ready_to_enable"
                      ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                      : "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                  }
                  data-testid="google-routes-enable-status-badge"
                >
                  {enablePreflight.status === "already_enabled"
                    ? "Already enabled"
                    : enablePreflight.status === "ready_to_enable"
                    ? "Ready to enable"
                    : "Blocked"}
                </Badge>
                <span className="text-zinc-400">·</span>
                <span className="text-zinc-300">
                  restart required:{" "}
                  <span data-testid="google-routes-restart-required">
                    {enablePreflight.restart_required ? "yes" : "no"}
                  </span>
                </span>
              </div>

              {enablePreflight.blockers?.length ? (
                <div
                  className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-2"
                  data-testid="google-routes-enable-blockers"
                >
                  <div className="text-rose-300 text-xs font-medium">
                    Blockers ({enablePreflight.blockers.length})
                  </div>
                  <ul className="mt-1 list-disc list-inside text-rose-200 text-xs space-y-0.5">
                    {enablePreflight.blockers.map((b) => (
                      <li
                        key={b}
                        data-testid={`google-routes-enable-blocker-${b}`}
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div
                className="mt-2"
                data-testid="google-routes-required-env-changes"
              >
                <div className="text-zinc-400 text-xs mb-1">
                  Required env changes
                </div>
                {(enablePreflight.required_env_changes || []).length === 0 ? (
                  <div className="text-xs text-zinc-500">
                    No env changes needed.
                  </div>
                ) : (
                  <ul className="text-xs space-y-0.5">
                    {(enablePreflight.required_env_changes || []).map((c) => (
                      <li
                        key={c.name}
                        className="font-mono text-zinc-300"
                        data-testid={`google-routes-env-${c.name}`}
                      >
                        {c.name} = {c.secret ? "<SECRET>" : c.target_value}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-2">
                <div className="text-zinc-400 text-xs mb-1">
                  Recommended steps
                </div>
                <ol
                  className="list-decimal list-inside text-zinc-300 text-xs space-y-0.5"
                  data-testid="google-routes-recommended-steps"
                >
                  {(enablePreflight.recommended_steps || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
              </div>

              <div className="mt-3 flex flex-col md:flex-row md:items-end gap-2">
                <div className="flex-1">
                  <Label
                    htmlFor="grs-enable-confirm"
                    className="text-xs text-zinc-400"
                  >
                    Confirmation phrase to unlock runbook copy
                  </Label>
                  <Input
                    id="grs-enable-confirm"
                    value={enableConfirmInput}
                    onChange={(e) => setEnableConfirmInput(e.target.value)}
                    placeholder={ENABLE_CONFIRM_PHRASE}
                    data-testid="google-routes-enable-confirm-input"
                  />
                </div>
                <Button
                  onClick={copyEnableRunbook}
                  disabled={
                    enableConfirmInput !== ENABLE_CONFIRM_PHRASE ||
                    !enablePreflight
                  }
                  data-testid="google-routes-copy-runbook-btn"
                >
                  <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy enable runbook
                </Button>
              </div>
              <div className="mt-2 text-xs text-zinc-500 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-400" />
                <span>
                  No API key is shown anywhere on this page. The
                  runbook lists env var names + target values only;
                  secrets are marked &lt;SECRET&gt; and must be
                  applied on the backend host manually.
                </span>
              </div>
            </>
          )}
        </div>

        {/* Post-enable health check */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="google-routes-health-check-section"
        >
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Post-enable health check
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            After restart, run the health check to confirm the
            provider is reachable. Reuses the origin / destination
            inputs above. Does NOT recalculate existing projects,
            quotes or invoices.
          </p>
          <Button
            size="sm"
            onClick={runHealthCheck}
            disabled={
              healthCheckLoading || !origin.trim() || !destination.trim()
            }
            data-testid="google-routes-health-check-btn"
          >
            <Play className="w-3.5 h-3.5 mr-1" />
            {healthCheckLoading ? "Checking…" : "Run health check"}
          </Button>

          {healthCheckError ? (
            <div
              className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300"
              data-testid="google-routes-health-check-error"
            >
              {healthCheckError}
            </div>
          ) : null}

          {healthCheckResult ? (
            healthCheckResult.ok ? (
              <div
                className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs"
                data-testid="google-routes-health-check-ok"
              >
                <div>
                  <div className="text-zinc-400">enabled</div>
                  <div className="text-zinc-300">
                    {String(healthCheckResult.enabled)}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">key configured</div>
                  <div className="text-zinc-300">
                    {String(healthCheckResult.api_key_configured)}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">distance / duration</div>
                  <div className="text-zinc-300">
                    {healthCheckResult.distance_km} km ·{" "}
                    {healthCheckResult.duration_minutes} min
                  </div>
                </div>
                <div>
                  <div className="text-zinc-400">cached</div>
                  <div className="text-zinc-300">
                    {String(healthCheckResult.cached)}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200"
                data-testid="google-routes-health-check-not-ok"
              >
                <div>
                  reason:{" "}
                  <code>{healthCheckResult.reason || "unknown"}</code>
                </div>
                {healthCheckResult.note ? (
                  <div className="mt-0.5">{healthCheckResult.note}</div>
                ) : null}
              </div>
            )
          ) : null}
        </div>
        {/* EVENTSFLO-GOOGLE-ROUTES-CACHE-INSIGHTS-V1 — read-only mini-panel */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="google-routes-cache-insights-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Cache insights
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadCacheInsights}
              disabled={cacheInsightsLoading}
              data-testid="google-routes-cache-insights-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Cache insights are read-only and show safe truncated
            labels only. No provider call is made. No historical
            records are recalculated.
          </p>

          {cacheInsightsError ? (
            <div
              className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300"
              data-testid="google-routes-cache-insights-error"
            >
              {cacheInsightsError}
            </div>
          ) : !cacheInsights ? (
            <div className="text-xs text-zinc-500">
              Loading cache insights…
            </div>
          ) : cacheInsights.recent_rows?.length === 0 ? (
            <div
              className="text-xs text-zinc-500"
              data-testid="google-routes-cache-insights-empty"
            >
              No cache rows yet. After Google Routes is enabled
              and a route lookup runs, the most recent rows will
              appear here.
            </div>
          ) : (
            <div
              className="overflow-auto rounded border border-zinc-700/40"
              data-testid="google-routes-cache-insights-table"
            >
              <table className="w-full text-xs">
                <thead className="bg-zinc-800/60 text-zinc-400">
                  <tr>
                    <th className="text-left px-2 py-1">key</th>
                    <th className="text-left px-2 py-1">route</th>
                    <th className="text-right px-2 py-1">dist / dur</th>
                    <th className="text-left px-2 py-1">fetched</th>
                    <th className="text-left px-2 py-1">state</th>
                  </tr>
                </thead>
                <tbody>
                  {cacheInsights.recent_rows.map((r) => (
                    <tr
                      key={r.cache_key_short}
                      className="odd:bg-zinc-900/30"
                      data-testid={`google-routes-cache-row-${r.cache_key_short}`}
                    >
                      <td className="px-2 py-1 font-mono text-zinc-300">
                        {r.cache_key_short}…
                      </td>
                      <td className="px-2 py-1 text-zinc-300">
                        {r.origin_label} → {r.destination_label}
                      </td>
                      <td className="px-2 py-1 text-right text-zinc-300">
                        {r.distance_km != null ? `${r.distance_km} km` : "—"} ·{" "}
                        {r.duration_minutes != null
                          ? `${r.duration_minutes} min`
                          : "—"}
                      </td>
                      <td className="px-2 py-1 text-zinc-400">
                        {fmt(r.fetched_at)}
                      </td>
                      <td className="px-2 py-1">
                        {r.is_expired ? (
                          <Badge
                            className="bg-rose-500/15 text-rose-300 border border-rose-500/30"
                            data-testid="google-routes-cache-row-expired-badge"
                          >
                            expired
                          </Badge>
                        ) : (
                          <Badge
                            className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            data-testid="google-routes-cache-row-valid-badge"
                          >
                            valid
                            {r.ttl_remaining_hours != null
                              ? ` · ${r.ttl_remaining_hours}h left`
                              : ""}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {cacheInsights ? (
            <div
              className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
              data-testid="google-routes-cache-insights-aggregates"
            >
              <div>
                <div className="text-zinc-400">last 24h</div>
                <div className="text-zinc-300">{cacheInsights.rows_last_24h}</div>
              </div>
              <div>
                <div className="text-zinc-400">last 7d</div>
                <div className="text-zinc-300">{cacheInsights.rows_last_7d}</div>
              </div>
              <div>
                <div className="text-zinc-400">expired (shown)</div>
                <div className="text-zinc-300">
                  {cacheInsights.expired_rows_in_window}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">malformed</div>
                <div className="text-zinc-300">
                  {cacheInsights.malformed_rows}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {/* EVENTSFLO-GOOGLE-ROUTES-FINGERPRINT-HISTORY-V1 — read-only validation history */}
        <div
          className="mt-3 rounded-md border border-zinc-700/40 p-3"
          data-testid="google-routes-fingerprint-history-section"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Fingerprint history
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadFpHistory}
              disabled={fpHistoryLoading}
              data-testid="google-routes-fingerprint-history-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh history
            </Button>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Read-only history of successful key validations. Only
            12-char SHA-256 prefixes are shown. The API key itself
            is never stored or displayed. No provider call is made.
          </p>

          {fpHistoryError ? (
            <div
              className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300"
              data-testid="google-routes-fingerprint-history-error"
            >
              {fpHistoryError}
            </div>
          ) : !fpHistory ? (
            <div className="text-xs text-zinc-500">
              Loading fingerprint history…
            </div>
          ) : fpHistory.rows?.length === 0 ? (
            <div
              className="text-xs text-zinc-500"
              data-testid="google-routes-fingerprint-history-empty"
            >
              No validation history yet. Run a successful
              post-enable health check to record the first entry.
            </div>
          ) : (
            <div
              className="overflow-auto rounded border border-zinc-700/40"
              data-testid="google-routes-fingerprint-history-table"
            >
              <table className="w-full text-xs">
                <thead className="bg-zinc-800/60 text-zinc-400">
                  <tr>
                    <th className="text-left px-2 py-1">validated at</th>
                    <th className="text-left px-2 py-1">fingerprint</th>
                    <th className="text-left px-2 py-1">source</th>
                    <th className="text-left px-2 py-1">actor</th>
                    <th className="text-left px-2 py-1">route</th>
                    <th className="text-right px-2 py-1">dist / dur</th>
                  </tr>
                </thead>
                <tbody>
                  {fpHistory.rows.map((r) => {
                    const matchesCurrent =
                      fpHistory.current_key_fingerprint_prefix &&
                      r.key_fingerprint_prefix ===
                        fpHistory.current_key_fingerprint_prefix;
                    return (
                      <tr
                        key={r.id || `${r.validated_at}-${r.key_fingerprint_prefix}`}
                        className="odd:bg-zinc-900/30"
                        data-testid={`google-routes-fingerprint-row-${r.key_fingerprint_prefix}`}
                      >
                        <td className="px-2 py-1 text-zinc-400">
                          {fmt(r.validated_at)}
                        </td>
                        <td className="px-2 py-1">
                          <span
                            className="font-mono text-zinc-300"
                            data-testid={`google-routes-fingerprint-prefix-${r.key_fingerprint_prefix}`}
                          >
                            {r.key_fingerprint_prefix}…
                          </span>
                          {matchesCurrent ? (
                            <Badge
                              className="ml-2 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                              data-testid={`google-routes-fingerprint-current-badge-${r.key_fingerprint_prefix}`}
                            >
                              current
                            </Badge>
                          ) : null}
                        </td>
                        <td className="px-2 py-1 text-zinc-400">
                          {r.api_key_source || "—"}
                        </td>
                        <td className="px-2 py-1 text-zinc-400">
                          {r.actor_email || "—"}
                        </td>
                        <td className="px-2 py-1 text-zinc-300">
                          {r.origin_label} → {r.destination_label}
                        </td>
                        <td className="px-2 py-1 text-right text-zinc-300">
                          {r.distance_km != null ? `${r.distance_km} km` : "—"}{" "}
                          ·{" "}
                          {r.duration_minutes != null
                            ? `${r.duration_minutes} min`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {fpHistory ? (
            <div
              className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
              data-testid="google-routes-fingerprint-history-aggregates"
            >
              <div>
                <div className="text-zinc-400">total recorded</div>
                <div className="text-zinc-300" data-testid="google-routes-fingerprint-history-total">
                  {fpHistory.total_count}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">shown</div>
                <div className="text-zinc-300">
                  {fpHistory.returned_count}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">limit</div>
                <div className="text-zinc-300">{fpHistory.limit}</div>
              </div>
              <div>
                <div className="text-zinc-400">malformed</div>
                <div className="text-zinc-300">
                  {fpHistory.malformed_rows}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
